<template>
  <!-- 图片放大预览：同卡片内可左右连着看（← → 或点两侧箭头，Esc / 点空白关闭） -->
  <div v-if="visible" class="rd-viewer" @click="close">
    <img
      class="rd-viewer__img"
      :src="current.src"
      :alt="current.alt"
      @click.stop
    />

    <!-- 多图时显示左右切换。
         图标用 SVG 描边（而不是「旋转的 border」或「× 字符」）：
         字形/边框旋转后墨迹中心与盒中心不一致，看起来不居中；SVG 的坐标能直接算准。 -->
    <template v-if="list.length > 1">
      <span class="rd-viewer__nav is-prev" @click.stop="prev">
        <svg class="rd-viewer__ico" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M13.5 3.5 L6.5 10 L13.5 16.5" />
        </svg>
      </span>
      <span class="rd-viewer__nav is-next" @click.stop="next">
        <svg
          class="rd-viewer__ico is-flip"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path d="M13.5 3.5 L6.5 10 L13.5 16.5" />
        </svg>
      </span>
      <span class="rd-viewer__counter"
        >{{ index + 1 }} / {{ list.length }}</span
      >
    </template>

    <span class="rd-viewer__close" @click.stop="close">
      <svg class="rd-viewer__ico" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M6 6 L14 14" />
        <path d="M14 6 L6 14" />
      </svg>
    </span>
    <span class="rd-viewer__hint">
      {{ list.length > 1 ? "← → 切换，点击空白处关闭" : "点击空白处关闭" }}
    </span>
  </div>
</template>

<script>
/**
 * 权益详情页配图的「点击查看大图」
 *
 * 说明：点击图片后全屏展示原图（原图为 3x，放到屏幕上比卡片里清楚得多）。
 * 只作用于 `.rd-media` 内的配图 —— 首屏 hero / 装饰图不参与。
 *
 * 同卡片内的多张图（如卡 4 的 4 张、卡 5 的 8 张、卡 8 的 2 张）可**连着看**：
 * 调用方把该卡片的图片列表与当前下标传进来，组件负责左右切换。
 */
export default {
  name: "RightsImageViewer",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 同卡片内的图片列表：[{ src, alt }]
    list: {
      type: Array,
      default: () => [],
    },
    // 当前展示的下标
    index: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    current() {
      return this.list[this.index] || {};
    },
  },
  watch: {
    visible(val) {
      // 打开时锁定页面滚动，避免背景跟着滚
      if (typeof document === "undefined") return;
      document.body.style.overflow = val ? "hidden" : "";
      // 键盘左右切换：预览是全屏浮层，监听 document 比给 div 加 tabindex 更可靠
      if (val) {
        document.addEventListener("keydown", this.onKeydown);
      } else {
        document.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeDestroy() {
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this.onKeydown);
    }
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
    },
    // 循环切换：最后一张再点「下一张」回到第一张
    prev() {
      const n = this.list.length;
      if (n < 2) return;
      this.$emit("update:index", (this.index - 1 + n) % n);
    },
    next() {
      const n = this.list.length;
      if (n < 2) return;
      this.$emit("update:index", (this.index + 1) % n);
    },
    onKeydown(e) {
      if (e.key === "ArrowLeft") {
        this.prev();
      } else if (e.key === "ArrowRight") {
        this.next();
      } else if (e.key === "Escape") {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.rd-viewer {
  position: fixed;
  inset: 0;
  z-index: 3000; // 高于 el-header(1000) 与 layui 弹层
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;

  &__img {
    // 留出边距，避免贴边；长图（手机截图约 1:2）按高度铺满更实用
    max-width: 92vw;
    max-height: 92vh;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    border-radius: 8px;
    cursor: default;
    // 不要给 <img> 加 background：卡 1/2/3/6/7/8 的切图自带圆角（四角 alpha=0，
    // 沿对角线有 15~31px 透明弧），填白底会在四角透出白色花瓣状残角。
    // 不带透明的切图（卡 4/5/9）内容本身就是不透明白，去掉底也不会变样。
  }

  // 左右切换热区：比圆钮大一圈，但不画底片（避免在图上又叠两块色斑）
  &__nav {
    position: absolute;
    top: 50%;
    // 图片与遮罩都在这层之下，热区用半透明圆钮保证任何图上都看得清
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.42);
    cursor: pointer;
    transition: background 0.2s;
    &.is-prev {
      left: 16px;
    }
    &.is-next {
      right: 16px;
    }
    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  }

  // 图标统一 20×20 viewBox；线条用 currentColor，尺寸/描边只在这里调
  &__ico {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #fff;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    // 右箭头复用同一段 path，水平翻转即可（避免写两套坐标）
    &.is-flip {
      transform: scaleX(-1);
    }
  }

  // 图最高 92vh，上下各只剩 ~4vh，浮层文字必然压在图上。
  // 手机截图底色是白的，白字直接落在上面等于看不见，故统一加半透明底片
  &__counter {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 3px 12px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.5);
    font-size: 14px;
    color: #fff;
    letter-spacing: 0.5px;
    pointer-events: none;
  }

  &__close {
    position: absolute;
    top: 16px;
    right: 20px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
      background: rgba(0, 0, 0, 0.72);
    }
    // 关闭图标比箭头略小，视觉重量才匹配
    .rd-viewer__ico {
      width: 14px;
      height: 14px;
    }
  }

  &__hint {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 3px 12px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.5);
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
    pointer-events: none;
  }
}
</style>
